// =============================================
// Author:      JUNAID HASSAN
// Create date: 2025-08-17
// Description: Contact tab bound to NEW API shape.
//              - Loads overlay payload: { data, flags, pendingCountContact, ... }
//              - Save posts a DRAFT, then re-reads overlay
//              - Shows a banner when there are unsent changes
//              - Lazy-loads only once when tab first becomes active
// =============================================

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  CSpinner, CFormInput, CBadge, CFormFeedback
} from '@coreui/react';
import { useTranslation } from '../../utils/i18nStub';
import { fetchContactOverlay, saveContactDraft } from '../../services/employeeService';
import { normalizeKsaMobile, isValidKsaMobile, fmtKsaMobile } from '../../utils/phone';

const empty = { email: '', mobile: '', other_mobile: '', emergency_name: '', emergency_phone: '' };

const EmployeeContactInfo = forwardRef(({ active = false, onLoadContact }, ref) => {
  const { t } = useTranslation(['contact', 'common']);

  // overlay (what the form binds to)
  const [form, setForm] = useState(empty);
  const [flags, setFlags] = useState({});
  const [meta, setMeta] = useState({ pendingCount: 0, lastSavedAt: null });

  // view state
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState(empty); // original for cancel
  const [errors, setErrors] = useState({ mobile: null, other_mobile: null, emergency_phone: null });
  const [loadedOnce, setLoadedOnce] = useState(false); // lazy-load guard

  // Validate and normalize to 9665XXXXXXXX; store bare error key like 'phone_invalid'
  const validateAndMaybeNormalize = (field, raw) => {
    if (!raw) {
      setErrors((e) => ({ ...e, [field]: null }));
      setForm((f) => ({ ...f, [field]: '' }));
      return;
    }
    const norm = normalizeKsaMobile(raw);
    if (!norm) {
      setErrors((e) => ({ ...e, [field]: 'phone_invalid' }));
      setForm((f) => ({ ...f, [field]: raw }));
    } else {
      setErrors((e) => ({ ...e, [field]: null }));
      setForm((f) => ({ ...f, [field]: norm }));
    }
  };

  useImperativeHandle(ref, () => ({
    // Parent expects a mobile number; give the overlay value (string)
    getMobileNumber: () => (form?.mobile || '').toString(),
    // Optional: allow parent to force-refresh
    refreshOverlay: async () => { await loadOverlay(true); },    
    // NEW: allow parent to read the overlay (data + flags + meta)
    getOverlaySnapshot: () => ({
      data: { ...form },
      flags: { ...flags },
      meta:  { ...meta },
    }),
    
  }));

  const loadOverlay = async (force = false) => {
    if (!active && !force) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchContactOverlay();
      const data = payload?.data || empty; // prefer data; you can fallback to payload.contact if you expose it

      setForm({
        email: data.email ?? '',
        mobile: data.mobile ?? '',
        other_mobile: data.other_mobile ?? '',
        emergency_name: data.emergency_name ?? '',
        emergency_phone: data.emergency_phone ?? '',
      });
      setFlags(payload?.flags || {});
      setMeta({
        pendingCount: payload?.pendingCountContact ?? 0, // contact-only count
        lastSavedAt: payload?.lastSavedAt ?? null,
      });
      setSnapshot({
        email: data.email ?? '',
        mobile: data.mobile ?? '',
        other_mobile: data.other_mobile ?? '',
        emergency_name: data.emergency_name ?? '',
        emergency_phone: data.emergency_phone ?? '',
      });

      if (onLoadContact && data?.mobile) onLoadContact(String(data.mobile));
      setLoadedOnce(true);
    } catch (err) {
      console.error('fetchContactOverlay error', err);
      setError(err?.message || 'Failed to load contact info');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load once when the tab first becomes active
  useEffect(() => {
    if (!active || loadedOnce) return;
    loadOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loadedOnce]);

  const handleEdit = () => {
    setSnapshot({ ...form });
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setForm({ ...snapshot });
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setWorking(true);
    setError(null);
    try {
      const payload = await saveContactDraft(form); // posts draft, re-fetches overlay on server and returns payload
      const data = payload?.data || empty;

      setForm({
        email: data.email ?? '',
        mobile: data.mobile ?? '',
        other_mobile: data.other_mobile ?? '',
        emergency_name: data.emergency_name ?? '',
        emergency_phone: data.emergency_phone ?? '',
      });
      setFlags(payload?.flags || {});
      setMeta({
        pendingCount: payload?.pendingCountContact ?? 0,
        lastSavedAt: payload?.lastSavedAt ?? null,
      });
      setSnapshot({
        email: data.email ?? '',
        mobile: data.mobile ?? '',
        other_mobile: data.other_mobile ?? '',
        emergency_name: data.emergency_name ?? '',
        emergency_phone: data.emergency_phone ?? '',
      });
      setIsEditing(false);

      if (onLoadContact) onLoadContact(String(data.mobile ?? ''));
    } catch (err) {
      console.error('saveContactDraft error', err);
      setError(err?.message || 'Update failed');
    } finally {
      setWorking(false);
    }
  };

  const fmtDate = (d) => (d ? String(d).split('T')[0] : '—');

  // Before first activation render nothing; after that keep the component mounted (cached)
  if (!active && !loadedOnce) return null;
  if (loading) return <CSpinner />;

  return (
    <div className="space-y-4" dir="rtl">
      {meta.pendingCount > 0 && (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3">
          {t('common:unsent_changes_with_count', { count: meta.pendingCount })}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h5 className="mb-0 text-xl font-bold text-gray-900">{t('tabs.contact', 'بيانات التواصل')}</h5>
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('btn.edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={working || errors.mobile || errors.other_mobile || errors.emergency_phone}
              className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
            >
              {working ? <CSpinner size="sm" /> : t('btn.save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {t('btn.cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-danger mb-2">{error}</div>}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              key: 'mobile',
              label: t('mobile', 'رقم الجوال'),
              placeholder: '05XXXXXXXX',
              value: form.mobile,
              formatter: fmtKsaMobile,
              type: 'tel',
              errorKey: 'mobile',
            },
            {
              key: 'other_mobile',
              label: t('other_mobile', 'جوال إضافي'),
              placeholder: '05XXXXXXXX',
              value: form.other_mobile,
              formatter: fmtKsaMobile,
              type: 'tel',
              errorKey: 'other_mobile',
            },
            {
              key: 'email',
              label: t('email', 'البريد الإلكتروني'),
              placeholder: t('email', 'example@domain.sa'),
              value: form.email,
              type: 'email',
            },
            {
              key: 'emergency_name',
              label: t('emergency_name', 'اسم جهة الاتصال للطوارئ'),
              placeholder: t('emergency_name', 'اسم جهة الاتصال للطوارئ'),
              value: form.emergency_name,
              type: 'text',
            },
            {
              key: 'emergency_phone',
              label: t('emergency_phone', 'هاتف جهة الاتصال للطوارئ'),
              placeholder: '05XXXXXXXX',
              value: form.emergency_phone,
              type: 'tel',
              errorKey: 'emergency_phone',
            },
          ].map((field) => {
            const errorMessage = field.errorKey ? errors[field.errorKey] : null;
            const draftFlag = flags?.[field.key] === 'draft';
            const displayValue = field.formatter ? field.formatter(field.value) : field.value;
            return (
              <div key={field.key} className="flex flex-col text-right">
                <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                  {field.label}
                  {!isEditing && displayValue && draftFlag && (
                    <CBadge color="info" className="text-xs">{t('common:draft', 'مسودة')}</CBadge>
                  )}
                </span>
                <CFormInput
                  value={isEditing ? field.value || '' : displayValue || ''}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    handleChange(field.key, v);
                    if (field.errorKey === 'mobile' || field.errorKey === 'other_mobile') {
                      setErrors((err) => ({ ...err, [field.errorKey]: v ? (isValidKsaMobile(v) ? null : 'phone_invalid') : null }));
                    }
                    if (field.errorKey === 'emergency_phone') {
                      setErrors((err) => ({ ...err, emergency_phone: v ? (isValidKsaMobile(v) ? null : 'phone_invalid') : null }));
                    }
                  }}
                  onBlur={field.key === 'mobile' || field.key === 'other_mobile' || field.key === 'emergency_phone'
                    ? (e) => validateAndMaybeNormalize(field.key, e.currentTarget.value)
                    : undefined}
                  readOnly={!isEditing}
                  disabled={!isEditing}
                  type={field.type}
                  inputMode={field.type === 'tel' ? 'numeric' : undefined}
                  placeholder={field.placeholder}
                  invalid={isEditing && !!errorMessage}
                  className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                />
                {isEditing && errorMessage && (
                  <CFormFeedback invalid>{t(`common:errors.${errorMessage}`, 'رقم غير صحيح')}</CFormFeedback>
                )}
              </div>
            );
          })}

          <div className="flex flex-col text-right">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('updated_on', 'آخر تحديث')}</span>
            <CFormInput
              value={fmtDate(meta.lastSavedAt) || ''}
              readOnly
              disabled
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default EmployeeContactInfo;
